import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle } from 'lucide-react';
import { Player, TeamsResponse, SelectionData, Team, SkillDistribution } from '../Model';
import { getSkillColor } from '../skillNames';
import { useDragHandlers } from './useDragHandlers';
import { restClient } from '../RestClient';
import TeamToolBar from './TeamToolBar';
import { SkillDistributionSlider } from './SkillDistributionSlider';

function DraggablePlayer({ player, onRemovePlayer, skillDistribution }: { player: Player; onRemovePlayer?: (playerId: number) => void; skillDistribution?: SkillDistribution }) {
  const [hover, setHover] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `player-${player.id}` });

  const colors = getSkillColor(player, skillDistribution);

  // Simplified inline styles - clean, borderless design with accent bar
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderRadius: 6,
    padding: '8px 12px',
    paddingLeft: '16px',
    marginBottom: 4,
    cursor: 'grab',
    fontSize: 14,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: isDragging ? 'var(--color-surface)' : hover ? 'var(--color-surface-hover)' : 'transparent',
    opacity: isDragging ? 0.8 : 1,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
    color: 'var(--color-text)',
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onRemovePlayer?.(player.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`${colors.category} (${player.totalScore})`}
    >
      <div
        className="player-accent-bar"
        style={{ backgroundColor: colors.accent }}
      />
      <div className="team-player-name">
        {player.name}
        {player.isUnknown && (
          <span title="Auto-generated player with default skills">
            <AlertTriangle size={12} className="unknown-player-icon" />
          </span>
        )}
      </div>
      <div className="team-player-right">
        <span className="team-player-score">{player.totalScore}</span>
        {onRemovePlayer && (
          <button
            onClick={handleRemoveClick}
            className="team-player-remove"
            style={{ opacity: hover ? 1 : 0 }}
            title="Remove from teams"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

function DroppableTeam({
  team,
  players,
  onRemovePlayer,
  skillDistribution,
  maxTeamScore
}: {
  team: Team;
  players: Player[];
  onRemovePlayer: (playerId: number) => void;
  skillDistribution?: SkillDistribution;
  maxTeamScore?: number;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `team-${team.id}`,
  });

  const teamScore = players.reduce((sum, p) => sum + p.totalScore, 0);
  const scoreBarWidth = maxTeamScore && maxTeamScore > 0 ? (teamScore / maxTeamScore) * 100 : 0;

  return (
    <div
      ref={setNodeRef}
      className={`team-column ${isOver ? 'team-column-drop-active' : ''}`}
    >
      <div className="team-header">
        <h3
          className="team-title"
          style={{ color: team.color, borderBottom: `2px solid ${team.color}` }}
        >
          {team.name} ({players.length})
        </h3>
      </div>

      {/* Team Score Bar */}
      {players.length > 0 && (
        <div className="team-score-section">
          <div className="team-score-header">
            <span className="team-score-value">{teamScore} pts</span>
          </div>
          <div className="team-score-bar-track">
            <div
              className="team-score-bar-fill"
              style={{
                width: `${scoreBarWidth}%`,
                backgroundColor: team.color
              }}
            />
          </div>
        </div>
      )}

      <SortableContext items={players.map(p => `player-${p.id}`)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col">
          {players.map((p) => <DraggablePlayer key={p.id} player={p} onRemovePlayer={onRemovePlayer} skillDistribution={skillDistribution} />)}
          {players.length === 0 && (
            <div className="drop-zone">
              Drop players here
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

type TeamsProps = {
  selectionData: SelectionData | null;
  onError?: (message: string) => void;
  onSelectionDataChange?: (selectionData: SelectionData) => void;
  onMaxPlayersChange?: (maxPlayers: number) => void;
  onSkillDistributionChange?: (distribution: SkillDistribution) => void;
};

export function Teams({ selectionData, onError, onSelectionDataChange, onMaxPlayersChange, onSkillDistributionChange }: TeamsProps) {
  const [teamsResponse, setTeamsResponse] = useState<TeamsResponse | null>(null);
  const [maxPlayers, setMaxPlayers] = useState<number>(9);
  const [numberOfTeams, setNumberOfTeams] = useState<number>(2);
  const [assignedTeams, setAssignedTeams] = useState<Team[]>([]);
  const [skillDistribution, setSkillDistribution] = useState<SkillDistribution>({
    developing: 15,
    average: 45,
    good: 85
  });

  // Sync assignedTeams with selectionData.teams when it changes
  React.useEffect(() => {
    if (selectionData?.teams) {
      setAssignedTeams(selectionData.teams);
    }
  }, [selectionData?.teams]);

  // Notify parent when maxPlayers changes
  React.useEffect(() => {
    onMaxPlayersChange?.(maxPlayers);
  }, [maxPlayers, onMaxPlayersChange]);

  // Notify parent of initial skill distribution
  React.useEffect(() => {
    onSkillDistributionChange?.(skillDistribution);
  }, [onSkillDistributionChange, skillDistribution]);

  const { activePlayer, handleDragStart, handleDragEnd } = useDragHandlers(
    assignedTeams,
    setAssignedTeams
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleAssignTeams = async () => {
    try {
      if (!selectionData) {
        onError?.('No selection data available');
        return;
      }

      const cleanSkillDistribution = {
        developing: Math.round(skillDistribution.developing),
        average: Math.round(skillDistribution.average),
        good: Math.round(skillDistribution.good)
      };

      // Filter out archived players - they shouldn't be included in team balancing
      const players = (selectionData.players || []).filter(p => !p.isArchived);
      const result = await restClient.assignTeams(players, maxPlayers, numberOfTeams, selectionData.teams, selectionData.id, cleanSkillDistribution);
      if (result.IsSuccess) {
        const data = result.Value;
        setTeamsResponse(data);
        const teamArray = data.teams || [];
        setAssignedTeams(teamArray);

        if (onSelectionDataChange) {
          const updatedSelectionData: SelectionData = {
            ...selectionData,
            teams: teamArray
          };
          onSelectionDataChange(updatedSelectionData);
        }
      } else {
        console.error('Error assigning teams:', result.Error);
        onError?.(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error assigning teams:', error);
      onError?.('Unexpected error occurred while assigning teams');
    }
  };

  const handleAssignTeamsFromText = async (text: string) => {
    try {
      if (!selectionData) {
        onError?.('No selection data available');
        return;
      }

      const cleanSkillDistribution = {
        developing: Math.round(skillDistribution.developing),
        average: Math.round(skillDistribution.average),
        good: Math.round(skillDistribution.good)
      };

      const result = await restClient.assignTeamsFromText(text, maxPlayers, numberOfTeams, selectionData.teams, selectionData.id, cleanSkillDistribution);
      if (result.IsSuccess) {
        const data = result.Value;
        setTeamsResponse(data);
        const teamArray = data.teams || [];
        setAssignedTeams(teamArray);

        if (onSelectionDataChange) {
          const updatedSelectionData: SelectionData = {
            ...selectionData,
            teams: teamArray
          };
          onSelectionDataChange(updatedSelectionData);
        }
      } else {
        console.error('Error assigning teams from text:', result.Error);
        onError?.(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error assigning teams from text:', error);
      onError?.('Unexpected error occurred while assigning teams from text');
    }
  };

  const handleRemovePlayer = (playerId: number) => {
    const updatedTeams = assignedTeams.map(team => ({
      ...team,
      players: team.players?.filter(player => player.id !== playerId) || []
    }));

    setAssignedTeams(updatedTeams);

    if (selectionData && onSelectionDataChange) {
      const updatedSelectionData: SelectionData = {
        ...selectionData,
        teams: updatedTeams
      };
      onSelectionDataChange(updatedSelectionData);
    }
  };

  const handleSkillDistributionChange = (distribution: SkillDistribution) => {
    setSkillDistribution(distribution);
    onSkillDistributionChange?.(distribution);
  };

  return (
    <>
      <SkillDistributionSlider
        distribution={skillDistribution}
        onDistributionChange={handleSkillDistributionChange}
        skillNames={selectionData?.skillNames}
      />
      <TeamToolBar
        players={selectionData?.players || []}
        maxPlayers={maxPlayers}
        setMaxPlayers={setMaxPlayers}
        numberOfTeams={numberOfTeams}
        setNumberOfTeams={setNumberOfTeams}
        handleAssignTeams={handleAssignTeams}
        handleAssignTeamsFromText={handleAssignTeamsFromText}
        teams={assignedTeams}
        showCaptureButton={teamsResponse !== null}
        selectionData={selectionData}
        onError={onError}
      />
      {teamsResponse && assignedTeams.length > 0 && (() => {
        // Calculate team scores for balance visualization
        const teamScores = assignedTeams.map(team =>
          (team.players || []).reduce((sum, p) => sum + p.totalScore, 0)
        );
        const maxTeamScore = Math.max(...teamScores, 1);
        const minTeamScore = Math.min(...teamScores);
        const scoreDifference = maxTeamScore - minTeamScore;

        return (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Balance Summary */}
          <div className="teams-balance-summary">
            {scoreDifference === 0 ? (
              <span className="teams-balance-perfect">Perfectly balanced</span>
            ) : (
              <span className="teams-balance-diff">
                Balance: <strong>{scoreDifference}</strong> pts difference
              </span>
            )}
          </div>

          <div
            className="teams-display-grid"
            style={{ gridTemplateColumns: `repeat(${Math.min(assignedTeams.length, 3)}, 1fr)` }}
          >
            {assignedTeams.map((team, index) => {
              const teamInfo = selectionData?.teams?.find(t => t.id === team.id) ||
                              { id: team.id, version: team.version, name: team.name || `Team ${index + 1}`, color: team.color || '#343a40',
                              selectionDataId: team.selectionDataId };

              return (
                <DroppableTeam
                  key={team.id}
                  team={teamInfo}
                  players={team.players || []}
                  onRemovePlayer={handleRemovePlayer}
                  skillDistribution={skillDistribution}
                  maxTeamScore={maxTeamScore}
                />
              );
            })}
          </div>

          <DragOverlay>
            {activePlayer ? (() => {
              const dragColors = getSkillColor(activePlayer, skillDistribution);
              return (
                <div
                  style={{
                    borderRadius: 6,
                    padding: '8px 12px',
                    paddingLeft: '16px',
                    cursor: 'grabbing',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    background: 'var(--color-surface)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    color: 'var(--color-text)',
                    minWidth: '150px',
                    position: 'relative',
                  }}
                >
                  <div
                    className="player-accent-bar"
                    style={{ backgroundColor: dragColors.accent }}
                  />
                  <div className="team-player-name">
                    {activePlayer.name}
                    {activePlayer.isUnknown && (
                      <span title="Auto-generated player">
                        <AlertTriangle size={12} className="unknown-player-icon" />
                      </span>
                    )}
                  </div>
                  <span className="team-player-score">{activePlayer.totalScore}</span>
                </div>
              );
            })() : null}
          </DragOverlay>
        </DndContext>
        );
      })()}
    </>
  );
}
